/*
 * Alters the behavior of the uiSelect npm module.
 * Provides with pagination with infinite scroll to handle large lists of choices.
 * An upfront large list of choices makes the control unstable and unresponsive.
 * This feature avoids populaing the list upfront by pagination which is the primary cause of unstability.
 *
 * Only changes things when `all-choices` attribute is added to the ui-select-choices directive
 *
 * Pagination works in 2 scenarios:-
 *
 * 1) Simple scrolling of the contents.
 * 2) Scrolling when the Autocomplete/search text is extended and the results are still too large.
 *
 * @example
    <ui-select-choices position="up" all-choices="ctrl.allTenThousandItems"  refresh-delay="0"
        repeat="person in $select.pageOptions.people | propsFilter: {name: $select.search, age: $select.search} ">
      <div ng-bind-html="person.name | highlight: $select.search"></div>
      <small>
        email: {{person.email}}
        age: <span ng-bind-html="''+person.age | highlight: $select.search"></span>
      </small>
    </ui-select-choices>

   extended from: https://stackoverflow.com/a/38077388/3038518
*/

import angular from 'angular';

export default {
  name: 'uiSelectChoicesDirective',
  decorator
};

/* @ngInject */
function decorator($delegate, $parse, $timeout) {

  const [directive] = $delegate;
  const oldCompile = directive.compile;

  // replace the compile function
  directive.compile = function (...args) {
    const [tEl, tAttr] = args;
    const link = oldCompile.apply(this, args); // runs old compile, then adds to it

    if (angular.isDefined(tAttr.paginate)) {
      const choices = tEl.querySelectorAll('.ui-select-choices-row');
      const repeatExp = choices.attr('ng-repeat').replace('$select.items', '$select.pagedItems');
      choices.attr('ng-repeat', repeatExp).removeAttr('ng-if');
    }


    return function (scope, elm, attr, $select) {
      link.apply(this, arguments); // eslint-disable-line prefer-rest-params
      if (angular.isUndefined(attr.paginate)) {
        return;
      }
      const scrollThresholdPercent = 80;
      let scrollCompleted = true;
      scope.pagingOptions = { page: 0 };

      attr.refresh = 'addMoreItems()';
      const refreshCallBack = $parse(attr.refresh);


      scope.addMoreItems = function () {
        scope.pagingOptions.page++;
        updateList();
      };

      function updateList() {
        const { page } = scope.pagingOptions;
        const { items } = $select;
        const PAGE_SIZE = 20;
        $select.pagedItems = items.slice(0, page * PAGE_SIZE);
      }

      const throttleScroll = _.throttle((event) => {
        const { offsetHeight, scrollHeight, scrollTop } = elm[0];
        const remainingHeight = offsetHeight - scrollHeight;
        const percent = Math.abs((scrollTop / remainingHeight) * 100);

        if (scrollCompleted && percent >= scrollThresholdPercent) {
          scrollCompleted = false;
          event.preventDefault();
          event.stopPropagation();
          $timeout(() => {
            scope.addingMore = true;
            refreshCallBack(scope, {
              $event: event
            });
            scrollCompleted = true;
          });
        }
      }, 100);
      elm.bind('scroll', throttleScroll);


      const listeners = [
        scope.$on('uis:activate', updateList),
        scope.$on('uis:select', () => {
          $select.search = '';

          // Move cursor focus back to input field instead of choices list (ui-select bugfix)
          elm.parent().prev().children('li').children('input').focus();
        }),
        scope.$on('uis:refresh', updateList),
        scope.$on('uis:close', () => {
          scope.pagingOptions.page = 0;
        }),
      ];
      scope.$on('$destroy', () => {
        elm.off('scroll', throttleScroll);
        listeners.forEach(destroy => destroy());
      });
    };
  };
  return $delegate;
}
